import { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";
import {
  Sparkles,
  Search,
  Bot,
  Compass,
  TrendingUp,
  Target,
  CheckCircle2,
  ArrowRight,
  HelpCircle,
} from "lucide-react";

const PAGE_TITLE =
  "What Is AI Visibility? The New Way to Be Found by AI Assistants | uSeen";
const PAGE_DESC =
  "AI Visibility shows how visible, understandable and recommendable your business is across ChatGPT, Gemini, Google AI and other AI-powered answer engines.";

const FAQ_ITEMS = [
  {
    q: "What is AI Visibility?",
    a: "AI Visibility is the digital visibility layer that shows how visible, understandable and recommendable a business or brand is across AI-powered search and answer engines.",
  },
  {
    q: "Does AI Visibility replace SEO?",
    a: "No. SEO still matters. AI Visibility is a new visibility layer that sits on top of SEO. The goal is no longer just to rank on Google — it is to also be recommended inside AI answers.",
  },
  {
    q: "How can my business get recommended on ChatGPT?",
    a: "Your business information needs to be accurate, consistent and trustworthy across the web. Your website, Google Business Profile, customer reviews, social profiles and third-party mentions are all evaluated together as a whole.",
  },
  {
    q: "What does uSeen do?",
    a: "uSeen analyses how visible your business is across AI assistants, compares you with competitors, identifies the gaps and turns them into actionable tasks.",
  },
  {
    q: "Who is AI Visibility important for?",
    a: "It is especially critical for restaurants, clinics, beauty centres, hotels, camp sites, auto services, real-estate offices and other local service businesses. But it is becoming increasingly important for any brand that wants to be discoverable online.",
  },
];

/**
 * Public SEO landing page — /en/what-is-ai-visibility
 * No auth required. Marketing/guide tone. FAQPage JSON-LD embedded for rich result eligibility.
 * Mirrors /ai-visibility-nedir for English-speaking visitors.
 */
export default function AIVisibilityEn() {
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    document.title = PAGE_TITLE;
    if (metaDesc) metaDesc.setAttribute("content", PAGE_DESC);

    const tags = [
      ["property", "og:title", PAGE_TITLE],
      ["property", "og:description", PAGE_DESC],
      ["property", "og:type", "article"],
      ["name", "twitter:title", PAGE_TITLE],
      ["name", "twitter:description", PAGE_DESC],
    ];
    const added = [];
    for (const [attr, key, val] of tags) {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
        added.push(tag);
      }
      tag.setAttribute("content", val);
    }

    // hreflang alternates — links the English page to its Turkish counterpart.
    const ORIGIN = "https://useen.ai";
    const alternates = [
      { hreflang: "en", href: `${ORIGIN}/en/what-is-ai-visibility` },
      { hreflang: "tr", href: `${ORIGIN}/ai-visibility-nedir` },
      { hreflang: "x-default", href: `${ORIGIN}/en/what-is-ai-visibility` },
    ];
    document.querySelectorAll('link[rel="alternate"][data-useen-hreflang]').forEach((el) => el.remove());
    const addedLinks = [];
    alternates.forEach(({ hreflang, href }) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", hreflang);
      link.setAttribute("href", href);
      link.setAttribute("data-useen-hreflang", "1");
      document.head.appendChild(link);
      addedLinks.push(link);
    });

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-useen-faq", "ai-visibility-en");
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);

    const prevHtmlLang = document.documentElement.lang;
    document.documentElement.lang = "en";

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== undefined && prevDesc !== null) {
        metaDesc.setAttribute("content", prevDesc);
      }
      added.forEach((t) => t.parentNode?.removeChild(t));
      addedLinks.forEach((l) => l.parentNode?.removeChild(l));
      ld.parentNode?.removeChild(ld);
      document.documentElement.lang = prevHtmlLang;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-slate-200 flex flex-col" data-testid="ai-visibility-en-page">
      <div className="absolute inset-0 bg-grid opacity-[0.08] pointer-events-none" />

      <header className="relative z-10 px-6 lg:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center"><Logo /></Link>
        <nav className="flex items-center gap-5 text-sm">
          <Link to="/fiyatlandirma" className="text-slate-300 hover:text-cyan-300">Pricing</Link>
          <Link to="/giris" className="text-slate-300 hover:text-cyan-300">Log in</Link>
          <Link to="/kayit" className="neon-btn rounded-lg px-4 py-2 text-white text-sm font-medium" data-testid="header-cta-register">
            Start free
          </Link>
        </nav>
      </header>

      <main className="relative z-10 flex-1 px-6 lg:px-12 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-300 mb-3 inline-flex items-center gap-1.5">
            <Sparkles size={11} /> AI Visibility · Guide
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6">
            What Is AI Visibility?
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed mb-3">
            AI Visibility describes how visible, understandable and recommendable
            <strong className="text-white"> a brand, business or website is inside ChatGPT, Gemini, Google AI, Perplexity </strong>
            and other AI-powered answer engines.
          </p>
          <p className="text-slate-400 leading-relaxed mb-6">
            For years, businesses focused on SEO to climb to the top of Google.
            But users no longer only use traditional search — they ask AI assistants directly.
          </p>

          <ExampleQuestions />

          <p className="text-slate-300 leading-relaxed mt-6 mb-12">
            Showing up inside the answers to questions like these is the new digital battleground.
            <strong className="text-white"> AI Visibility is exactly about winning that battleground.</strong>
          </p>

          <Article>
            <Section icon={<TrendingUp size={18} />} title="Why AI Visibility Matters">
              <p>
                People are changing the way they look for information. Instead of scrolling through pages of
                search results, they ask AI assistants for direct recommendations.
              </p>
              <p>
                This creates a new reality for businesses: being on Google is no longer enough. Your business
                must also be understood, trusted and recommendable by AI engines.
              </p>
              <p>
                {"A business's visibility inside AI tools depends on the consistency of its website, Google Business Profile, customer reviews, social presence, third-party mentions and overall information across the web."}
              </p>
            </Section>

            <Section icon={<Compass size={18} />} title="What Does AI Visibility Cover?">
              <p>
                AI Visibility is broader than website SEO. It is a holistic digital visibility surface.
              </p>
              <BulletList items={[
                "Your website being machine-readable for AI tools",
                "Your business information being consistent across the web",
                "Your Google Business Profile being accurate and up to date",
                "Your customer reviews creating quality and trust signals",
                "Your brand being mentioned across credible sources",
                "Your services being clearly described and easy to summarise",
                "Owning content that answers real customer questions in your category",
                "AI tools being able to map you to the right category and use case",
              ]} />
              <p>
                In short, AI Visibility is about making your business
                <strong className="text-white"> discoverable, understandable and recommendable </strong>
                across the digital world.
              </p>
            </Section>

            <Section icon={<Search size={18} />} title="SEO vs. AI Visibility — What Is the Difference?">
              <p>
                <strong className="text-white">SEO</strong> aims to rank your website higher on traditional search engines like Google.
              </p>
              <p>
                <strong className="text-white">AI Visibility</strong> aims to increase how often your business is shown, understood
                and recommended inside AI-powered answer engines.
              </p>
              <div className="not-prose grid sm:grid-cols-2 gap-3 my-5">
                <ComparisonCard
                  badge="SEO"
                  badgeColor="text-slate-300 border-slate-500/30 bg-slate-500/10"
                  title="Ranking on Google"
                  desc="Aims to put your URL in the top 10 classic search results."
                />
                <ComparisonCard
                  badge="AI Visibility"
                  badgeColor="text-cyan-300 border-cyan-500/30 bg-cyan-500/10"
                  title="Being recommended by AI"
                  desc="Aims to get your business named inside ChatGPT / Gemini answers."
                />
              </div>
              <p>
                SEO still matters. But on top of SEO, you now also need to measure how AI tools see your business.
              </p>
            </Section>

            <Section icon={<Bot size={18} />} title="How Do AI Tools Evaluate a Business?">
              <p>
                {"AI tools don't rely on a single source when recommending a business. They evaluate many signals across the web. For example, for a restaurant, the following are typically relevant:"}
              </p>
              <BulletList items={[
                "A clear menu, address and contact details on the website",
                "Strong Google reviews",
                "Consistent information across different platforms",
                "An active and authentic social media presence",
                "Real, positive content created by customers about the business",
                "A clear answer to the question: \"what is this business best known for?\"",
              ]} />
              <p>
                Similar but adapted signals matter for clinics, hotels, beauty centres, camp sites and service firms.
              </p>
            </Section>

            <Section icon={<Target size={18} />} title="Where Does uSeen Fit In?">
              <p>
                <strong className="text-white">uSeen</strong> analyses how visible, understandable and recommendable
                {" your business is to AI engines. It doesn't just produce a report — it turns gaps into actionable tasks."}
              </p>
              <p>With uSeen you can see:</p>
              <BulletList items={[
                "How visible your business is across AI tools",
                "Whether your competitors are more visible than you",
                "Which of your details are missing or inconsistent",
                "Whether your website is being understood by AI",
                "Which customer intents you are strong or weak on",
                "Which content would lift your visibility the most",
                "Which actions should be prioritised first",
              ]} />
              <p>
                uSeen scans your digital presence, identifies the gaps and gives you a clear roadmap to be more
                visible inside AI engines.
              </p>
            </Section>

            <Section icon={<CheckCircle2 size={18} />} title="Who Is It For?">
              <p>
                AI Visibility is becoming critical for local and service-led businesses. uSeen is a great fit for:
              </p>
              <div className="not-prose grid grid-cols-2 sm:grid-cols-3 gap-2 my-4">
                {[
                  "Restaurants", "Cafés", "Clinics",
                  "Beauty centres", "Hotels", "Camp sites",
                  "Auto services", "Real-estate offices", "Private schools",
                  "Health & care", "Local services", "Other brands",
                ].map((label) => (
                  <div key={label} className="rounded-lg border border-white/5 bg-black/30 px-3 py-2 text-sm text-slate-300">
                    {label}
                  </div>
                ))}
              </div>
              <p>
                As more users ask AI tools for recommendations, this surface will become
                <strong className="text-white"> more important for every industry.</strong>
              </p>
            </Section>

            <Section title="The Bottom Line">
              <p>
                <strong className="text-white">AI Visibility is the new visibility surface of the digital world.</strong>
              </p>
              <p>
                {"Search isn't only \"ten blue links\" any more. Users increasingly get their answers, recommendations and comparisons directly from AI tools."}
              </p>
              <p>So the new question for businesses is:</p>
              <div className="not-prose my-5 space-y-2 text-base">
                <div className="rounded-xl border border-slate-500/20 bg-slate-500/5 px-4 py-3 text-slate-400 line-through decoration-slate-600">
                  {"\"Am I on Google?\""}
                </div>
                <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-3 text-white">
                  {"\"Do AI tools understand me, trust me, and are they ready to recommend me?\""}
                </div>
              </div>
              <p>
                uSeen helps you answer this question — and then close the gap.
              </p>
            </Section>
          </Article>

          {/* CTA */}
          <div
            className="mt-12 rounded-3xl border border-cyan-500/20 bg-cyan-500/5 p-7 sm:p-10 relative overflow-hidden"
            data-testid="ai-visibility-en-cta-block"
          >
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-cyan-500/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
                Start measuring your AI visibility
              </h2>
              <p className="text-slate-300 leading-relaxed max-w-2xl mb-6">
                Get a free check to see which customer intents your business is strong on, and where it is currently
                invisible to AI engines.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/kayit"
                  className="neon-btn rounded-xl px-5 py-3 text-white font-semibold inline-flex items-center gap-2"
                  data-testid="ai-visibility-en-cta-primary"
                >
                  Start free <ArrowRight size={14} />
                </Link>
                <Link
                  to="/fiyatlandirma"
                  className="ghost-btn rounded-xl px-5 py-3 text-white border border-white/10 inline-flex items-center gap-2"
                  data-testid="ai-visibility-en-cta-secondary"
                >
                  See plans
                </Link>
              </div>
              <div className="mt-6 text-sm text-slate-400">
                Keep reading: <Link to="/en/what-is-geo" className="text-cyan-300 hover:text-cyan-200" data-testid="ai-visibility-en-internal-geo">What is GEO?</Link>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <section className="mt-16" data-testid="ai-visibility-en-faq">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-purple-400 mb-3">
              <HelpCircle size={11} /> Frequently asked questions
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-6">
              Common questions about AI Visibility
            </h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-2xl border border-white/5 bg-black/30 px-5 py-4 open:bg-white/[0.03] transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-white font-medium">
                    <span>{f.q}</span>
                    <span className="text-cyan-300 text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-slate-300 leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
            <div className="mt-8 text-sm text-slate-400 text-center">
              Got more questions?{" "}
              <Link to="/iletisim" className="text-cyan-300 hover:text-cyan-200" data-testid="ai-visibility-en-contact-link">
                Get in touch
              </Link>
              .
            </div>
          </section>
        </div>
      </main>

      <SiteFooter lang="en" />
    </div>
  );
}

function Article({ children }) {
  return <article className="space-y-10 text-slate-300 leading-relaxed">{children}</article>;
}

function Section({ icon, title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl sm:text-[1.7rem] font-bold text-white mb-3 flex items-center gap-2.5">
        {icon && <span className="text-cyan-300">{icon}</span>}
        {title}
      </h2>
      <div className="space-y-3 text-[0.975rem]">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-1.5 my-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function ComparisonCard({ badge, badgeColor, title, desc }) {
  return (
    <div className="rounded-xl border border-white/5 bg-black/30 p-4">
      <div className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${badgeColor} mb-2`}>
        {badge}
      </div>
      <div className="text-white text-sm font-medium mb-1">{title}</div>
      <div className="text-xs text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}

function ExampleQuestions() {
  const examples = [
    "Recommend a good restaurant near me.",
    "Is there a trusted beauty centre in Istanbul?",
    "Which is the best camp site in this area?",
    "Who are the credible providers of this service nearby?",
  ];
  return (
    <div className="rounded-2xl border border-white/5 bg-black/30 p-5">
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-3 flex items-center gap-1.5">
        <Bot size={11} /> Example questions people ask AI
      </div>
      <ul className="space-y-2">
        {examples.map((q, i) => (
          <li key={i} className="text-slate-300 text-[0.95rem] flex items-start gap-2.5">
            <span className="text-cyan-400 shrink-0">›</span>
            <span>{`"${q}"`}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
